/**
 * React-hook-form registration for a money field, with the typing rules the
 * shared numeric kit already defines.
 *
 * The perps price and size fields were registered raw, so the field accepted
 * whatever the browser would hold:
 *  - `70000,55` in the limit price became **7000055.00** with the CTA enabled --
 *    a 100x order. (The field also asked for `type="number"`, which makes Chrome
 *    eat the comma before any handler sees it, so that had to go too; the shared
 *    `Input` already defaults to `text` for exactly this reason.)
 *  - `1e3` in the size field became a **$1,000 order**, `-5` rendered an Order
 *    Value of **-$5.00**, and `abc` / `1.2.3` sat in the field as typed.
 *
 * `sanitizeTyping` is deliberately used on every change rather than a blur-time
 * reformat: it strips what cannot belong in a number and otherwise leaves the
 * text alone, so the caret does not jump while the trader is still typing.
 *
 * Paste goes through `normalizePaste` instead, because the two rules differ.
 * `sanitizeTyping` replaces the FIRST comma with a dot (right for the European
 * decimal `70000,55`) which would mangle a pasted `1,234.56` into `1.23456`;
 * `normalizePaste` treats the LAST separator as the decimal point and handles
 * both conventions.
 *
 * A keystroke that would CHANGE the number if stripped — an exponent, a minus,
 * a second separator — is rejected outright and the field keeps what it held
 * ("1e3" used to become 13, "-1" 1, "1.2.3" 1.23: a different order, silently).
 * "What it held" is snapshotted from the element itself just before the edit
 * (focus, keydown, beforeinput), never remembered from our last onChange:
 * react-hook-form's setValue/reset — the blur floor, MAX and % chips, a market
 * switch — write the DOM without passing through onChange, so a remembered
 * value could be stale and the next keystroke would revert to it. With no
 * fresh snapshot (autofill, say) the old strip-only rule applies.
 */
import { ClipboardEvent, SyntheticEvent } from "react";
import { UseFormRegisterReturn } from "react-hook-form";
import { normalizePaste, sanitizeTyping } from "../../../utils/amount/sanitize";

/**
 * Generous on purpose. This guard exists to reject what is not a number at all;
 * how many decimals a particular market accepts is already decided downstream by
 * the form's own validators and by the step-flooring on submit. Picking a
 * tighter number here would quietly become a second, competing source of truth.
 * Perp prices and sizes are 1e18 across every chain.
 */
export const MAX_INPUT_DECIMALS = 18;

/** The field's value just before the edit in flight; consumed by onChange. */
const beforeEdit = new WeakMap<object, string>();

const snapshot = (event: SyntheticEvent<HTMLInputElement>) => {
  const el = event.currentTarget;
  if (el && typeof el.value === "string") beforeEdit.set(el, el.value);
};

export const sanitizedRegister = (
  register: UseFormRegisterReturn
): UseFormRegisterReturn & {
  onPaste: (event: ClipboardEvent<HTMLInputElement>) => void;
  onFocus: (event: SyntheticEvent<HTMLInputElement>) => void;
  onKeyDown: (event: SyntheticEvent<HTMLInputElement>) => void;
  onBeforeInput: (event: SyntheticEvent<HTMLInputElement>) => void;
} => ({
  ...register,
  onFocus: snapshot,
  onKeyDown: snapshot,
  onBeforeInput: snapshot,
  // Typed as react-hook-form's own `ChangeHandler` (via the spread), whose
  // event is structural -- `{ target, type }` -- not a React ChangeEvent.
  onChange: (event: { target: { value?: string } }) => {
    const el = event.target;
    if (typeof el?.value === "string") {
      const previous = beforeEdit.get(el);
      beforeEdit.delete(el);
      const clean = sanitizeTyping(el.value, MAX_INPUT_DECIMALS, previous);
      // Only touch the DOM when something was actually rejected: assigning
      // `value` moves the caret to the end, which would fight anyone editing
      // the middle of what they had already typed.
      if (clean !== el.value) el.value = clean;
    }
    return register.onChange(event);
  },
  onPaste: (event: ClipboardEvent<HTMLInputElement>) => {
    const pasted = event.clipboardData?.getData("text");
    if (pasted === undefined) return;
    const normalized = normalizePaste(pasted, MAX_INPUT_DECIMALS);
    event.preventDefault();
    // Unparseable paste is dropped rather than partially applied: half of a
    // mistyped number is a worse starting point than the field as it was.
    if (normalized === null) return;
    const el = event.currentTarget;
    el.value = normalized;
    register.onChange({ target: el, type: "change" });
  },
});
